// src/types/Warehouse.ts
export interface WarehouseAddress {
  street: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface WarehouseContact {
  phone: string;
  email: string;
  manager: string;
}

export interface WarehouseCapacity {
  total: number;
  used: number;
  available: number;
}

// Operating hours are set per-day so a branch can be open, say, Mon-Sat and
// closed Sunday rather than only having one flat open/close pair applied to
// every day of the week.
export const DAYS_OF_WEEK = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;
export type DayOfWeek = typeof DAYS_OF_WEEK[number];

export const DAY_LABELS: Record<DayOfWeek, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

export interface DayHours {
  open: string;
  close: string;
  closed: boolean;
}

export interface WarehouseOperatingHours {
  timezone: string;
  monday: DayHours;
  tuesday: DayHours;
  wednesday: DayHours;
  thursday: DayHours;
  friday: DayHours;
  saturday: DayHours;
  sunday: DayHours;
}

export const createDefaultDayHours = (): DayHours => ({
  open: '09:00',
  close: '18:00',
  closed: false,
});

export const createDefaultOperatingHours = (): WarehouseOperatingHours => ({
  timezone: 'UTC',
  monday: createDefaultDayHours(),
  tuesday: createDefaultDayHours(),
  wednesday: createDefaultDayHours(),
  thursday: createDefaultDayHours(),
  friday: createDefaultDayHours(),
  saturday: createDefaultDayHours(),
  sunday: createDefaultDayHours(),
});

export interface Warehouse {
  id?: string;
  name: string;
  code: string;
  description: string;
  address: WarehouseAddress;
  contact: WarehouseContact;
  capacity: WarehouseCapacity;
  isActive: boolean;
  operatingHours?: WarehouseOperatingHours;
  // Whether this branch/show room shows up on the public client-facing
  // "Branches" page. Defaults to false — staff opt in a branch once its
  // details are confirmed correct, rather than every branch being public
  // by default.
  displayOnWebsite?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  createdByName?: string;
  updatedBy?: string;
  updatedByName?: string;
}

export interface WarehouseFormData {
  name: string;
  code: string;
  description: string;
  address: WarehouseAddress;
  contact: WarehouseContact;
  capacity: {
    total: string;
    used: string;
  };
  isActive: boolean;
  operatingHours: WarehouseOperatingHours;
  displayOnWebsite: boolean;
}

export interface WarehouseStats {
  total: number;
  active: number;
  inactive: number;
  totalCapacity: number;
  usedCapacity: number;
  availableCapacity: number;
  utilizationRate: number;
}